/**
 * NotificationsPage — enable/disable web push + device survival guide.
 * The "keep notifications alive" instructions matter: Android aggressively
 * battery-optimizes PWAs, so default settings can silently kill notifications.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bell, Smartphone, Battery, ShieldCheck, Send } from 'lucide-react';
import { usePush } from '../hooks/usePush';
import { NotificationInbox } from '../components/NotificationInbox';

export default function NotificationsPage() {
  const { supported, enabled, permission, busy, enable, disable, sendTest } = usePush();
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);
  const [testing, setTesting] = useState(false);

  const onEnable = async () => {
    setFeedback(null);
    const r = await enable();
    setFeedback(r.ok
      ? { ok: true, msg: 'Notifications are ON for this device. Send a test to confirm it arrives.' }
      : { ok: false, msg: r.error || 'Could not enable notifications.' });
  };

  const onTest = async () => {
    setTesting(true);
    const r = await sendTest();
    setFeedback(r.ok
      ? { ok: true, msg: 'Test sent — check your notifications tray (may take a few seconds).' }
      : { ok: false, msg: r.error || 'Test failed.' });
    setTesting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex items-center gap-3 mb-6 min-w-0">
          <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
            <Bell className="h-6 w-6 text-amber-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">Push alerts</h1>
            <p className="text-sm text-gray-500 truncate">Push alerts on this device — incidents, expiring certs & more</p>
          </div>
        </div>

        {/* UAT-R2-002 web half: the durable in-app alert inbox — the alerts
            RAISED for this manager, verifiable even with no push/email. */}
        <NotificationInbox />

        <div className="bg-white rounded-xl shadow border border-gray-100 p-6 mb-6">
          {feedback && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${feedback.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {feedback.msg}
            </div>
          )}

          {!supported ? (
            <p className="text-sm text-gray-600">
              This browser/device doesn't support push notifications. On iPhone/iPad, add Conformara to your
              Home Screen first (Safari → Share → Add to Home Screen), then open it from there.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Push alerts on this device</p>
                  <p className="text-sm text-gray-500">
                    {enabled === null ? 'Checking…' : enabled ? 'Enabled on this device' : permission === 'denied' ? 'Blocked in browser settings' : 'Currently off for this device'}
                  </p>
                </div>
                {enabled ? (
                  <button onClick={disable} disabled={busy} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {busy ? '…' : 'Turn off'}
                  </button>
                ) : (
                  <button onClick={onEnable} disabled={busy} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                    {busy ? 'Enabling…' : permission === 'denied' ? 'Blocked — see guide below' : 'Turn on'}
                  </button>
                )}
              </div>
              {enabled && (
                <button onClick={onTest} disabled={testing} className="mt-4 inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50">
                  <Send className="h-4 w-4" /> {testing ? 'Sending…' : 'Send me a test notification'}
                </button>
              )}
            </>
          )}
        </div>

        {/* Device survival guide */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-4">
            <Smartphone className="h-5 w-5 text-blue-600" />
            Keep notifications working on your phone
          </h2>
          <p className="text-sm text-gray-500 mb-5">
            Phone makers try to save battery by putting apps to sleep. These settings stop Conformara's
            alerts from being silenced. Do this once per device.
          </p>

          <div className="space-y-5 text-sm">
            <div>
              <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2"><ShieldCheck className="h-4 w-4 text-green-600" /> Android (Chrome)</h3>
              <ol className="list-decimal ml-5 space-y-1.5 text-gray-600">
                <li>Open conformara.com in Chrome → tap <strong>⋮ → Add to Home screen</strong> → Install. Installed PWAs get full notification support.</li>
                <li>When asked, <strong>allow notifications</strong> (or: long-press the Conformara home-screen icon → App info → Notifications → Allow).</li>
                <li><strong>Battery:</strong> App info → Battery → set to <strong>Unrestricted</strong> (Samsung: "Battery → Allow background usage → All"). This is the #1 setting that prevents silent kills.</li>
                <li>If your phone has "Put unused apps to sleep" (Samsung/Xiaomi), disable it for Conformara, or lock the app in recents.</li>
                <li>Don't swipe Conformara away from recents if you rely on urgent alerts — locked or unrestricted apps survive fine.</li>
              </ol>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2"><ShieldCheck className="h-4 w-4 text-green-600" /> iPhone / iPad (Safari)</h3>
              <ol className="list-decimal ml-5 space-y-1.5 text-gray-600">
                <li>Requires iOS 16.4+. Open conformara.com in <strong>Safari</strong> → Share → <strong>Add to Home Screen</strong>.</li>
                <li>Open Conformara <strong>from the home-screen icon</strong> (not Safari tabs) → turn on notifications here. iOS only allows push for installed web apps.</li>
                <li>Check <strong>Settings → Notifications → Conformara</strong> → allow, with Lock Screen + Banners enabled.</li>
                <li>Focus/Do-Not-Disturb modes silence everything, including Conformara — add Conformara to allowed apps for your work Focus.</li>
              </ol>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-2"><Battery className="h-4 w-4 text-yellow-600" /> Desktop (Chrome/Edge)</h3>
              <ol className="list-decimal ml-5 space-y-1.5 text-gray-600">
                <li>Keep the browser signed in and running (notifications arrive even when the tab is closed).</li>
                <li>If Windows shows "Focus assist" / "Do not disturb", Conformara alerts follow those rules — allow notifications during work hours.</li>
                <li>Site permission check: click the 🔒 icon in the address bar → Notifications → Allow.</li>
              </ol>
            </div>
          </div>

          <div className="mt-5 p-3 rounded-lg bg-blue-50 text-blue-800 text-xs">
            <strong>Tip:</strong> after changing battery settings, use "Send me a test notification" above to confirm alerts come through.
          </div>
        </div>
      </div>
    </div>
  );
}
